/** Numeric outputs are explicit for structured indicators; an empty list means a scalar. */
export const indicatorOutputFields = {
    sma: [],
    ema: [],
    rsi: [],
    vwma: [],
    atr: [],
    lwti: [],
    vma: [],
    macd: ['macd', 'signal', 'histogram'],
    stochastic: ['k', 'd'],
    stochasticrsi: ['k', 'd'],
    bollinger: ['upper', 'middle', 'lower'],
    donchian: ['upper', 'middle', 'lower'],
    keltner: ['upper', 'middle', 'lower'],
    adx: ['adx', 'plusDi', 'minusDi'],
    ichimoku: ['tenkan', 'kijun', 'senkouA', 'senkouB', 'chikou'],
    vwap: ['vwap', 'upper1', 'lower1', 'upper2', 'lower2', 'stdDev']
};
const unreachableIndicator = (indicator) => {
    throw new Error(`Unsupported indicator: ${JSON.stringify(indicator)}`);
};
const indicatorParameterFields = {
    sma: ['period'],
    ema: ['period'],
    rsi: ['period'],
    vwma: ['period'],
    atr: ['period'],
    lwti: ['period'],
    vma: ['period'],
    donchian: ['period'],
    adx: ['period'],
    vwap: [],
    macd: ['fastPeriod', 'slowPeriod', 'signalPeriod'],
    stochastic: ['kPeriod', 'dPeriod', 'lookback'],
    stochasticrsi: ['rsiPeriod', 'stochasticPeriod', 'kPeriod', 'dPeriod'],
    bollinger: ['period', 'multiplier'],
    keltner: ['emaPeriod', 'atrPeriod', 'multiplier'],
    ichimoku: ['tenkanPeriod', 'kijunPeriod', 'senkouBPeriod', 'shift']
};
/** Validate the shared wire contract before generating a key or submitting work. */
export function validateIndicatorInput(indicator) {
    if (!indicator || !Object.hasOwn(indicatorParameterFields, indicator.type))
        throw new Error('Unsupported indicator type');
    const fields = indicatorParameterFields[indicator.type];
    if (Object.keys(indicator).some((field) => field !== 'type' && !fields.includes(field))) {
        throw new Error(`Unknown parameter for indicator ${indicator.type}`);
    }
    for (const field of fields) {
        const value = Object.getOwnPropertyDescriptor(indicator, field)?.value;
        if (typeof value !== 'number' || !Number.isFinite(value))
            throw new Error(`Indicator ${field} must be finite`);
        if (field === 'multiplier') {
            if (value <= 0)
                throw new Error('Indicator multiplier must be greater than zero');
        }
        else if (!Number.isSafeInteger(value) || value < (field === 'shift' ? 0 : 1)) {
            throw new Error(`Indicator ${field} must be a ${field === 'shift' ? 'nonnegative' : 'positive'} safe integer`);
        }
    }
    // Exhaustiveness is intentional: a new IndicatorInput variant must update this contract.
    switch (indicator.type) {
        case 'sma':
        case 'ema':
        case 'rsi':
        case 'vwma':
        case 'atr':
        case 'lwti':
        case 'vma':
        case 'macd':
        case 'stochastic':
        case 'stochasticrsi':
        case 'bollinger':
        case 'donchian':
        case 'keltner':
        case 'adx':
        case 'ichimoku':
        case 'vwap':
            return;
        default:
            unreachableIndicator(indicator);
    }
}
const period = (value) => (value && /^\d+$/.test(value) ? Number(value) : Number.NaN);
const multiplier = (value) => (value && /^\d+(?:\.\d+)?$/.test(value) ? Number(value) : Number.NaN);
// Rust f64 Display uses decimal notation; keep small/large positive multipliers identical in JS.
export function formatIndicatorParameter(value) {
    const text = String(value);
    if (!/[eE]/.test(text))
        return text;
    const [coefficient = '', exponent = '0'] = text.split(/[eE]/);
    const [whole = '', fraction = ''] = coefficient.split('.');
    const digits = whole + fraction;
    const point = whole.length + Number(exponent);
    if (point <= 0)
        return `0.${'0'.repeat(-point)}${digits}`;
    if (point >= digits.length)
        return digits + '0'.repeat(point - digits.length);
    return `${digits.slice(0, point)}.${digits.slice(point)}`;
}
// The mapped type makes adding a union member without a decoder a build failure.
const decoders = {
    sma: (p) => (p.length === 1 ? { type: 'sma', period: period(p[0]) } : null),
    ema: (p) => (p.length === 1 ? { type: 'ema', period: period(p[0]) } : null),
    rsi: (p) => (p.length === 1 ? { type: 'rsi', period: period(p[0]) } : null),
    vwma: (p) => (p.length === 1 ? { type: 'vwma', period: period(p[0]) } : null),
    atr: (p) => (p.length === 1 ? { type: 'atr', period: period(p[0]) } : null),
    donchian: (p) => (p.length === 1 ? { type: 'donchian', period: period(p[0]) } : null),
    adx: (p) => (p.length === 1 ? { type: 'adx', period: period(p[0]) } : null),
    lwti: (p) => (p.length === 1 ? { type: 'lwti', period: period(p[0]) } : null),
    vma: (p) => (p.length === 1 ? { type: 'vma', period: period(p[0]) } : null),
    vwap: (p) => (p.length === 0 ? { type: 'vwap' } : null),
    macd: (p) => (p.length === 3 ? { type: 'macd', fastPeriod: period(p[0]), slowPeriod: period(p[1]), signalPeriod: period(p[2]) } : null),
    stochastic: (p) => p.length === 3 ? { type: 'stochastic', kPeriod: period(p[0]), dPeriod: period(p[1]), lookback: period(p[2]) } : null,
    stochasticrsi: (p) => p.length === 4
        ? {
            type: 'stochasticrsi',
            rsiPeriod: period(p[0]),
            stochasticPeriod: period(p[1]),
            kPeriod: period(p[2]),
            dPeriod: period(p[3])
        }
        : null,
    bollinger: (p) => (p.length === 2 ? { type: 'bollinger', period: period(p[0]), multiplier: multiplier(p[1]) } : null),
    ichimoku: (p) => p.length === 4
        ? { type: 'ichimoku', tenkanPeriod: period(p[0]), kijunPeriod: period(p[1]), senkouBPeriod: period(p[2]), shift: period(p[3]) }
        : null,
    keltner: (p) => p.length === 3 ? { type: 'keltner', emaPeriod: period(p[0]), atrPeriod: period(p[1]), multiplier: multiplier(p[2]) } : null
};
const isIndicatorType = (type) => Object.hasOwn(decoders, type);
/** Decode parameterized keys only. Legacy aliases belong at their feature-path boundary. */
export function indicatorInputFromKey(key) {
    const [type, ...parameters] = key.split('-');
    if (!type || !isIndicatorType(type))
        return null;
    const input = decoders[type](parameters);
    if (!input)
        return null;
    try {
        validateIndicatorInput(input);
    }
    catch {
        return null;
    }
    if (parameters.some((parameter) => formatIndicatorParameter(Number(parameter)) !== parameter))
        return null;
    return input;
}
export function isIndicatorOutputSupported(input, output) {
    const fields = indicatorOutputFields[input.type];
    return fields.length === 0 ? output == null : typeof output === 'string' && fields.includes(output);
}
/** Validate identity and output independently from whether market data is available. */
export function validateIndicatorValueRef(key, output) {
    const input = indicatorInputFromKey(key);
    if (!input)
        throw new Error(`Invalid canonical indicator key: ${key}`);
    if (!isIndicatorOutputSupported(input, output)) {
        throw new Error(`Indicator ${key} requires ${indicatorOutputFields[input.type].length ? `one of these outputs: ${indicatorOutputFields[input.type].join(', ')}` : 'a scalar value without an output selector'}`);
    }
    return input;
}
/** Missing/non-finite evidence remains missing, never a substituted default or object. */
export function readIndicatorNumericValue(key, output, value) {
    const input = indicatorInputFromKey(key);
    if (!input || !isIndicatorOutputSupported(input, output))
        return null;
    if (output != null) {
        if (typeof value !== 'object' || value === null || Array.isArray(value) || !('type' in value) || value.type !== input.type)
            return null;
        value = Object.getOwnPropertyDescriptor(value, output)?.value;
    }
    return typeof value === 'number' && Number.isFinite(value) ? value : null;
}
/** Legacy numeric feature paths support only exact scalar keys that both runtimes materialize.
 * Structured outputs must use trade.indicator.value with an explicit output selector.
 */
export function indicatorInputFromLegacyFeaturePath(path) {
    const input = indicatorInputFromKey(path);
    return input && isIndicatorOutputSupported(input) ? input : null;
}
//# sourceMappingURL=indicatorContract.js.map